import { LAST_WRECK_CONTENT } from '../content';
import { salvageValue, type GameEvent, type GameState } from '../systems';
import type { ContextActionState, EventMessage, LastWreckHudState, RunResultState } from '../ui';

const TUTORIAL_COPY = {
  'scan-hatch': [
    '1 · Scan the hatch',
    'Tap here. The sealed hatch is directly right of your drone.',
  ],
  'cut-hatch': [
    '2 · Cut the cyan seam',
    'Tap once. The cutter opens the marked hatch automatically.',
  ],
  'grapple-cassette': [
    '3 · Grapple the yellow cassette',
    'Fly beside the yellow cassette, then tap here to attach.',
  ],
  'deposit-cassette': [
    'Tow cassette to the green ring',
    'Move left. The cable pulls the cassette into extraction.',
  ],
  complete: ['Recover the navigation core', 'Choose a route through the eight-room wreck.'],
} as const;

function required<T>(value: T | undefined, label: string): T {
  if (value === undefined) throw new Error(`Missing authored runtime state: ${label}`);
  return value;
}

function eventMessage(event: GameEvent, index: number): EventMessage {
  const id = `${event.type}-${String(index)}`;
  switch (event.type) {
    case 'COMMAND_REJECTED':
      return { id, title: 'COMMAND BLOCKED', detail: event.reason, tone: 'warning' };
    case 'SCAN_PULSE':
      return { id, title: 'SCANNER RETURN', detail: `Geometry resolved in ${event.roomId}.` };
    case 'TARGET_LOCATED':
      return { id, title: 'CONTRACT TARGET', detail: 'Navigation core signature confirmed.' };
    case 'CUT_PROGRESS':
      return {
        id,
        title: event.valid ? 'SEAM ACCEPTED' : 'ALIGNMENT ERROR',
        detail: `${String(Math.round(event.progress * 100))}% cut · ${String(Math.round(event.heat * 100))} heat`,
        tone: event.valid ? 'info' : 'warning',
      };
    case 'CUT_COMPLETE':
      return { id, title: 'BREAK POINT OPEN', detail: event.cutPointId, tone: 'success' };
    case 'CUTTER_OVERHEAT':
      return {
        id,
        title: 'CUTTER OVERHEAT',
        detail: 'Release the trigger to cool.',
        tone: 'warning',
      };
    case 'PRESSURE_EQUALIZED':
      return {
        id,
        title: 'PRESSURE EVENT',
        detail: `${event.from} vented toward ${event.to}.`,
        tone: 'warning',
      };
    case 'POWER_CHANGED':
      return { id, title: `BUS ${event.bus}`, detail: `Power state ${event.state}.` };
    case 'JUMPER_STARTED':
      return {
        id,
        title: 'PORTABLE JUMPER',
        detail: `${String(event.seconds)} seconds of local power.`,
      };
    case 'JUMPER_EXPIRED':
      return { id, title: 'JUMPER EXPIRED', detail: event.deviceId, tone: 'warning' };
    case 'GRAPPLE_ATTACHED':
      return { id, title: 'CABLE LOCKED', detail: event.salvageId, tone: 'success' };
    case 'GRAPPLE_TENSION':
      return {
        id,
        title: event.warning ? 'CABLE STRAIN' : 'TENSION NOMINAL',
        detail: `${String(Math.round(event.tension * 100))}% load`,
        tone: event.warning ? 'warning' : 'info',
      };
    case 'CABLE_BROKE':
      return { id, title: 'CABLE FAILURE', detail: event.salvageId, tone: 'warning' };
    case 'SALVAGE_BANKED':
      return {
        id,
        title: 'EXTRACTION LOCK',
        detail: `¤ ${String(event.value)} secured.`,
        tone: 'success',
      };
    case 'INSTABILITY_THRESHOLD':
      return {
        id,
        title: `STRUCTURAL EVENT ${String(event.threshold)}`,
        detail: event.threshold >= 85 ? 'Extraction strongly advised.' : 'Wreck topology changed.',
        tone: 'warning',
      };
    case 'TUTORIAL_ADVANCED':
      return {
        id,
        title: 'CONTROL VERIFIED',
        detail: TUTORIAL_COPY[event.step][0],
        tone: 'success',
      };
    case 'RUN_ENDED':
      return { id, title: 'RUN COMPLETE', detail: event.result.ending, tone: 'success' };
  }
}

function currentContext(state: GameState): ContextActionState | undefined {
  const [label, hint] = TUTORIAL_COPY[state.tutorialStep];
  if (state.phase === 'tutorial') {
    const tutorialCut = state.cuts['tutorial-hatch-seam'];
    return {
      label,
      hint,
      enabled: true,
      ...(state.tutorialStep === 'cut-hatch' && tutorialCut
        ? { progress: tutorialCut.progress }
        : {}),
    };
  }
  if (state.currentRoomId === 'power-control') {
    return {
      label: state.power.buses.A === 'off' ? 'Restore power bus A' : 'Switch to power bus B',
      hint: 'Power changes routes and adds instability',
      enabled: true,
      danger: true,
    };
  }
  if (state.currentRoomId === LAST_WRECK_CONTENT.extractionRoomId) {
    return {
      label: state.salvage['navigation-core'].banked
        ? 'Leave the wreck'
        : 'Extraction cradle ready',
      hint: state.salvage['navigation-core'].banked
        ? 'End the run and bank the current score'
        : 'Tow salvage into the marked cradle',
      enabled: state.salvage['navigation-core'].banked || state.grapple !== null,
    };
  }
  return undefined;
}

function resultState(state: GameState, bestScore: number): RunResultState | undefined {
  if (!state.result) return undefined;
  const result = state.result;
  const coreBanked = state.salvage['navigation-core'].banked;
  const conditionBonus = coreBanked && result.coreCondition >= 80 ? 400 : 0;
  return {
    outcome: result.ending === 'contract-failed' ? 'failed' : result.ending,
    score: result.score,
    bankedValue: result.bankedSalvageValue,
    timeBonus: Math.max(0, 900 - result.elapsedSeconds) * 2,
    conditionBonus,
    coreCondition: result.coreCondition,
    elapsedSeconds: result.elapsedSeconds,
    isBestScore: result.score > bestScore,
  };
}

export function gameStateToHud(
  state: GameState,
  events: readonly GameEvent[],
  options: { readonly mapVisible: boolean; readonly paused: boolean; readonly bestScore: number },
): LastWreckHudState {
  const bankedValue = LAST_WRECK_CONTENT.salvage.reduce((total, definition) => {
    const runtime = state.salvage[definition.id];
    return runtime.banked ? total + salvageValue(definition.baseValue, runtime.condition) : total;
  }, 0);
  const grappled = state.grapple
    ? LAST_WRECK_CONTENT.salvage.find((item) => item.id === state.grapple?.salvageId)
    : undefined;
  const currentCuts = LAST_WRECK_CONTENT.cutPoints.filter(
    (point) => point.roomId === state.currentRoomId,
  );
  const currentHeat = currentCuts.reduce(
    (maximum, point) =>
      Math.max(maximum, required(state.cuts[point.id], point.id).heat / point.heatLimit),
    0,
  );
  const [title, instruction] = TUTORIAL_COPY[state.tutorialStep];
  const targetFound = state.stats.targetFirstLocatedAt !== null;
  const result = resultState(state, options.bestScore);
  const contextAction = currentContext(state);

  return {
    phase: result
      ? result.outcome === 'failed'
        ? 'failure'
        : 'success'
      : options.paused
        ? 'paused'
        : 'active',
    objective: {
      title: targetFound ? 'Extract the navigation core' : title,
      instruction: targetFound
        ? 'Detach both clamps, grapple the core, and return to the docking throat.'
        : instruction,
      step:
        state.tutorialStep === 'complete'
          ? 3
          : ['scan-hatch', 'cut-hatch'].includes(state.tutorialStep)
            ? 1
            : 2,
      totalSteps: 3,
      targetFound,
      complete: state.salvage['navigation-core'].banked,
    },
    instability: state.instability.value,
    elapsedSeconds: state.elapsedSeconds,
    ...(state.instability.emergencyRemainingSeconds === null
      ? {}
      : { emergencySeconds: state.instability.emergencyRemainingSeconds }),
    extraction: {
      bankedValue,
      targetValue: 1200,
      coreBanked: state.salvage['navigation-core'].banked,
      ...(grappled ? { cargoLabel: grappled.name, cargoMass: grappled.mass } : {}),
    },
    tool: {
      selected: state.selectedTool,
      energy: Math.max(0, 100 - state.scannerCooldownSeconds * 55),
      heat: currentHeat * 100,
      disabled: state.toolDisruptionSeconds > 0,
      ...(state.selectedTool === 'cutter'
        ? {
            validTarget: currentCuts.some(
              (point) => !required(state.cuts[point.id], point.id).complete,
            ),
          }
        : {}),
      ...(state.grapple ? { grappleTension: Math.min(100, state.grapple.tension * 100) } : {}),
    },
    map: {
      visible: options.mapVisible,
      scanProgress: Math.min(1, state.stats.scans / LAST_WRECK_CONTENT.rooms.length),
      rooms: LAST_WRECK_CONTENT.rooms.map((room) => {
        const runtime = state.rooms[room.id];
        const powered = room.bus !== null && state.power.buses[room.bus] === 'restored';
        return {
          id: room.id,
          label: room.name,
          x: 26 + room.position.x * 118,
          y: 34 + room.position.y * 96,
          width: 102,
          height: 72,
          state:
            room.id === state.currentRoomId
              ? 'current'
              : runtime.pressure === 'vacuum'
                ? 'hazard'
                : powered
                  ? 'powered'
                  : runtime.revealedUntil > state.elapsedSeconds
                    ? 'scanned'
                    : 'unknown',
          salvageCount: LAST_WRECK_CONTENT.salvage.filter(
            (item) => state.salvage[item.id].roomId === room.id && !state.salvage[item.id].banked,
          ).length,
        };
      }),
      connections: LAST_WRECK_CONTENT.connections.map((connection) => ({
        from: connection.from,
        to: connection.to,
        open: required(state.connections[connection.id], connection.id).open,
        cuttable: connection.kind === 'cut',
      })),
    },
    ...(contextAction ? { contextAction } : {}),
    events: events.slice(-2).map(eventMessage),
    ...(result ? { result } : {}),
  };
}
